import {
    createColumns,
    DataTableCustomized,
} from '@/components/ui/data-table-customized'
import { AsyncFilterSelect } from '@/components/ui/filters/async-filter-select'
import { DEPARTMENT_IDS } from '@/constants/departments'
import {
    getEmployeeContains,
    getEmployeesOptionsPaginated,
} from '@/modules/Employee/services'
import { Pencil, Eye, Trash2, Upload } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Typogaphy } from 'vnetwork-ui'
import type { EditableCard } from './scan-card-types'
import { SelectField } from '@/components/ui/select'

// Preview text for select fields — shows label or gray italic placeholder
const PreviewText: React.FC<{ value?: string; placeholder?: string }> = ({
    value,
    placeholder = '—',
}) =>
    value ? (
        <span className="text-xs">{value}</span>
    ) : (
        <span className="text-xs text-gray-400 italic">{placeholder}</span>
    )

// Preview for sale followers — resolves IDs to names
const SaleFollowersPreview: React.FC<{ value: number[] }> = ({ value }) => {
    const [labels, setLabels] = useState<string[]>([])

    useEffect(() => {
        if (!value.length) {
            setLabels([])
            return
        }
        getEmployeeContains({ employee_ids: value }).then((options) =>
            setLabels(options.map((o: { label: string }) => o.label))
        )
    }, [value])

    if (!value.length) {
        return <span className="text-xs text-gray-400 italic">—</span>
    }
    return <span className="text-xs">{labels.join(', ') || '...'}</span>
}

// Thin wrapper: bridges AsyncFilterSelect (needs form control) with external state
const SaleFollowersCell: React.FC<{
    value: number[]
    onChange: (v: number[]) => void
}> = ({ value, onChange }) => {
    const { control, setValue, watch } = useForm<{ ids: number[] }>({
        defaultValues: { ids: value },
    })
    const ids = watch('ids')

    // Sync external → form
    useEffect(() => {
        setValue('ids', value)
    }, [value, setValue])

    // Sync form → external
    useEffect(() => {
        if (JSON.stringify(ids) !== JSON.stringify(value)) onChange(ids)
    }, [ids])

    return (
        <AsyncFilterSelect
            control={control}
            name="ids"
            asyncOptions={(query, page) =>
                getEmployeesOptionsPaginated(
                    query,
                    [
                        DEPARTMENT_IDS.ACCOUNT,
                        DEPARTMENT_IDS.SALES,
                        DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
                    ],
                    'active',
                    page
                )
            }
            getDefaultOptions={(values: number[]) =>
                getEmployeeContains({ employee_ids: values })
            }
            multiple
            showClearButton={false}
        />
    )
}

interface PreviewStepProps {
    cards: EditableCard[]
    selectedKeys: string[]
    canImport: boolean
    importing: boolean
    customerSourceOptions: { value: string; label: string }[]
    industryOptions: { value: string; label: string }[]
    selectPlaceholder: string
    onSelectionChange: (keys: string[]) => void
    onUpdateCard: (id: string, field: string, value: any) => void
    onBulkUpdate: (field: string, value: any) => void
    onRemoveCard: (id: string) => void
    onImport: () => void
    onClose: () => void
    t: (key: string) => string
    tGlobal: (key: string) => string
}

const ScanCardPreviewStep: React.FC<PreviewStepProps> = ({
    cards,
    selectedKeys,
    canImport,
    importing,
    customerSourceOptions,
    industryOptions,
    selectPlaceholder,
    onSelectionChange,
    onUpdateCard,
    onBulkUpdate,
    onRemoveCard,
    onImport,
    onClose,
    t,
    tGlobal,
}) => {
    const [isEditing, setIsEditing] = useState(false)
    const selectedCount = selectedKeys.length
    const validCount = cards.filter((c) => !c._error).length

    // Helper to find label from options array
    const findLabel = (
        options: { value: string; label: string }[],
        value: string
    ) => options.find((o) => o.value === value)?.label || ''

    const columns = useMemo(
        () =>
            createColumns<EditableCard>([
                {
                    key: '_filename',
                    title: 'File',
                    width: 100,
                    pinned: 'left',
                    cellClassName: 'truncate',
                    render: (_, row) => (
                        <span
                            className={`text-xs truncate ${row._error ? 'text-red-500' : 'text-gray-600'}`}
                        >
                            {row._filename}
                        </span>
                    ),
                },
                {
                    key: 'customer_name',
                    title: t('customer_name'),
                    width: 230,
                    cellClassName: 'truncate',
                    render: (_, row) =>
                        row._error ? (
                            <span className="text-xs text-red-400 italic">
                                {row._error}
                            </span>
                        ) : (
                            <span className="text-xs">
                                {row.customer_name || '—'}
                            </span>
                        ),
                },
                {
                    key: 'customer_email',
                    title: t('customer_email'),
                    width: 180,
                    cellClassName: 'truncate',
                    render: (_, row) =>
                        row._error ? null : (
                            <span className="text-xs">
                                {row.customer_email || '—'}
                            </span>
                        ),
                },
                {
                    key: 'customer_phone',
                    title: t('customer_phone'),
                    width: 130,
                    cellClassName: 'truncate',
                    render: (_, row) =>
                        row._error ? null : (
                            <span className="text-xs">
                                {row.customer_phone || '—'}
                            </span>
                        ),
                },
                {
                    key: 'customer_position',
                    title: t('customer_position'),
                    width: 130,
                    cellClassName: 'truncate',
                    render: (_, row) =>
                        row._error ? null : (
                            <span className="text-xs">
                                {row.customer_position || '—'}
                            </span>
                        ),
                },
                {
                    key: 'customer_website',
                    title: t('customer_website'),
                    width: 160,
                    cellClassName: 'truncate',
                    render: (_, row) =>
                        row._error ? null : (
                            <span className="text-xs">
                                {row.customer_website || '—'}
                            </span>
                        ),
                },
                {
                    key: 'customer_industry',
                    title: t('customer_industry'),
                    width: 220,
                    cellClassName: 'truncate',
                    render: (_, row) =>
                        row._error ? null : isEditing ? (
                            <SelectField
                                size="sm"
                                value={row.customer_industry}
                                options={industryOptions}
                                placeholder={selectPlaceholder}
                                onValueChange={(v) =>
                                    onUpdateCard(
                                        row._id,
                                        'customer_industry',
                                        v
                                    )
                                }
                            />
                        ) : (
                            <PreviewText
                                value={findLabel(
                                    industryOptions,
                                    row.customer_industry
                                )}
                            />
                        ),
                },
                {
                    key: 'customer_source',
                    title: t('customer_source'),
                    width: 180,
                    cellClassName: 'truncate',
                    render: (_, row) =>
                        row._error ? null : isEditing ? (
                            <SelectField
                                size="sm"
                                value={row.customer_source}
                                options={customerSourceOptions}
                                placeholder={selectPlaceholder}
                                onValueChange={(v) =>
                                    onUpdateCard(row._id, 'customer_source', v)
                                }
                            />
                        ) : (
                            <PreviewText
                                value={findLabel(
                                    customerSourceOptions,
                                    row.customer_source
                                )}
                            />
                        ),
                },
                {
                    key: 'customer_sale_followers' as any,
                    title: t('sales_assignee'),
                    width: 250,
                    render: (_, row) =>
                        row._error ? null : isEditing ? (
                            <SaleFollowersCell
                                value={row.customer_sale_followers}
                                onChange={(v) =>
                                    onUpdateCard(
                                        row._id,
                                        'customer_sale_followers',
                                        v
                                    )
                                }
                            />
                        ) : (
                            <SaleFollowersPreview
                                value={row.customer_sale_followers}
                            />
                        ),
                },
            ]),
        [
            t,
            tGlobal,
            customerSourceOptions,
            industryOptions,
            selectPlaceholder,
            onUpdateCard,
            isEditing,
        ]
    )

    return (
        <div className="space-y-2">
            {/* Summary */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <Typogaphy className="text-xs font-medium text-gray-700">
                        {t('scan_card.results')} — {selectedCount}/{validCount}{' '}
                        {t('scan_card.selected').toLowerCase()}
                    </Typogaphy>
                    <button
                        type="button"
                        onClick={() => setIsEditing((v) => !v)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border border-gray-300 hover:bg-gray-50 text-gray-600"
                        title={isEditing ? 'Preview' : 'Edit'}
                    >
                        {isEditing ? (
                            <Eye className="w-3 h-3" />
                        ) : (
                            <Pencil className="w-3 h-3" />
                        )}
                    </button>
                </div>
                {!canImport && selectedCount > 0 && (
                    <span className="text-xs text-orange-600">
                        {t('scan_card.fill_required')}
                    </span>
                )}
            </div>

            {/* Bulk apply controls */}
            <div className="flex items-end gap-3 p-2 bg-gray-50 rounded border border-gray-200">
                <Typogaphy className="text-xs font-medium text-gray-500 pb-1 whitespace-nowrap">
                    {t('scan_card.apply_all')}:
                </Typogaphy>
                <div className="flex-1 min-w-0">
                    <label className="text-[10px] text-gray-500 mb-0.5 block">
                        {t('customer_industry')}
                    </label>
                    <SelectField
                        size="sm"
                        value={
                            industryOptions.find(
                                (o) => o.value === 'customer_industry'
                            )?.value
                        }
                        options={industryOptions}
                        placeholder={selectPlaceholder}
                        onValueChange={(v) =>
                            onBulkUpdate('customer_industry', v)
                        }
                    />
                </div>
                <div className="flex-1 min-w-0">
                    <label className="text-[10px] text-gray-500 mb-0.5 block">
                        {t('customer_source')}
                    </label>
                    <SelectField
                        size="sm"
                        value={
                            customerSourceOptions.find(
                                (o) => o.value === 'customer_source'
                            )?.value
                        }
                        options={customerSourceOptions}
                        placeholder={selectPlaceholder}
                        onValueChange={(v) =>
                            onBulkUpdate('customer_source', v)
                        }
                    />
                </div>
                <div className="flex-1 min-w-0">
                    <label className="text-[10px] text-gray-500 mb-0.5 block">
                        {t('sales_assignee')}
                    </label>
                    <SaleFollowersCell
                        value={[]}
                        onChange={(v) =>
                            onBulkUpdate('customer_sale_followers', v)
                        }
                    />
                </div>
            </div>

            {/* Data table */}
            <DataTableCustomized<EditableCard>
                columns={columns}
                rows={cards}
                rowKey={(row) => row._id}
                maxHeight="50vh"
                pinnedHeader
                bordered
                showIndex
                selection={{
                    selectedKeys,
                    onSelectionChange,
                    getRowKey: (row) => row._id,
                    canSelect: (row) => !row._error,
                    disabledReason: (row) =>
                        row._error ? t('scan_card.error') : undefined,
                }}
                actions={[
                    {
                        key: 'delete',
                        label: tGlobal('delete'),
                        icon: <Trash2 className="w-3.5 h-3.5" />,
                        variant: 'danger',
                        onClick: (row) => onRemoveCard(row._id),
                    },
                ]}
                rowClassName={(row) =>
                    row._error ? 'bg-red-50 opacity-60' : ''
                }
            />

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outlined" onClick={onClose}>
                    {tGlobal('cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={onImport}
                    disabled={!canImport || importing}
                    loading={importing}
                    startIcon={<Upload className="w-4 h-4" />}
                >
                    {t('scan_card.import_selected')} ({selectedCount})
                </Button>
            </div>
        </div>
    )
}

export default ScanCardPreviewStep
